import { useNuxtApp } from 'nuxt/app';
import { readonly, ref } from 'vue';
import type { ApiInstance } from '~/types/plugins/api';

/**
 * useGetData:
 * This function is used to handle fetching data from the server in SSR mode
 * It takes the request and the options for the fetch
 * It returns the data and the error
 */
export async function useGetData<T = unknown>(
  request?: string,
  opts?: Record<string, unknown>,
) {
  // Extract immediate option, default to true when request is provided
  const { immediate = request !== undefined, ...restOpts } = opts || {};
  const { $apiClient } = useNuxtApp() as unknown as { $apiClient: ApiInstance };

  // Set default options
  const defaultOpts = {
    headers: {
      'Content-Type': 'application/json',
    },
    immediate: Boolean(immediate),
  };

  // Return reactive state with execute function for deferred execution
  const result = ref<T | null>(null);
  const pending = ref(false);
  const error = ref<unknown>(null);

  // Execute function that can be called with parameters
  const execute = async (
    executeRequest?: string,
    executeOpts?: Record<string, unknown>,
  ) => {
    const finalRequest = executeRequest || request;

    if (!finalRequest) {
      throw new Error('Request is required for useGetData');
    }

    const finalOpts = {
      ...defaultOpts,
      ...restOpts,
      ...executeOpts,
      immediate: Boolean(executeOpts?.immediate ?? immediate),
    };

    return await $apiClient.get<T>(finalRequest, finalOpts);
  };

  // If we have an initial request, return the useFetch result
  if (request) {
    result.value = (await execute(request, {
      ...defaultOpts,
      ...restOpts,
      immediate: Boolean(immediate),
    })) as Awaited<ReturnType<typeof useFetch<T>>>;
  }

  return {
    ...(result.value as T),
    pending: readonly(pending),
    error: readonly(error),
    execute,
    refresh: () => execute(),
  };
}
